import { Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterDateRange, FilterField, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { SearchableSelect } from '../../../components/Select'
import ShortText from '../../../components/ShortText'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatDateTime, useAppToast, useDownload, useHasRole } from '../../../helpers'
import { TestimonialVideoService } from '../../../services/TestimonialVideo/TestimonialVideoService'
import { ITestimonialVideoCourseOption, ITestimonialVideoListItem } from '../../../services/TestimonialVideo/types'
import { MODALITY_OPTIONS } from './consts'
import TestimonialVideoEditModal from './TestimonialVideoEditModal'
import TestimonialVideoViewModal from './TestimonialVideoViewModal'

const COLUMNS = 8

/** `length` stays out: it is the page size chosen in the Paginator, not a filter. */
const INITIAL_FILTERS = {
  search: '',
  modality: '',
  courseId: '',
  dateStart: '',
  dateEnd: ''
}

function TestimonialVideosPage() {
  const service = new TestimonialVideoService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const { download } = useDownload()
  const permissions = {
    edit: hasRole('edit_testimonial_videos'),
    delete: hasRole('delete_testimonial_videos')
  }

  const [submissions, setSubmissions] = useState<ITestimonialVideoListItem[]>([])
  const [courses, setCourses] = useState<ITestimonialVideoCourseOption[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState({ length: DEFAULT_PAGE_SIZE, ...INITIAL_FILTERS })
  const isDirty = (Object.keys(INITIAL_FILTERS) as (keyof typeof INITIAL_FILTERS)[]).some(
    (key) => filters[key] !== INITIAL_FILTERS[key]
  )
  // Changing a filter goes back to page 1.
  const updateFilters = (patch: Partial<typeof filters>) => {
    setFilters({ ...filters, ...patch })
    setPage(1)
  }
  // Clearing keeps the page size the reader chose.
  const clearFilters = () => updateFilters(INITIAL_FILTERS)

  const [viewingId, setViewingId] = useState<number | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [editingId, setEditingId] = useState<number | null>(null)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState<ITestimonialVideoListItem | null>(null)

  const fetchSubmissions = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await service.list({
        search: filters.search,
        modality: filters.modality,
        course_id: filters.courseId,
        date: { start: filters.dateStart, end: filters.dateEnd },
        page,
        page_size: filters.length
      })
      setTotalPages(Math.ceil(data.recordsFiltered / filters.length) || 1)
      setSubmissions(data.data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os vídeos de depoimento', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchCourses = () => {
    service
      .getFilterCourses()
      .then(setCourses)
      .catch((error) => toast.error('Erro ao carregar os cursos', error))
  }

  // The search arrives already debounced from FilterSearch.
  useEffect(() => {
    fetchSubmissions()
  }, [page, filters])

  useEffect(() => {
    fetchCourses()
  }, [])

  // Deep link from the Marketing notification e-mail: /videos-de-depoimento?video={id}
  useEffect(() => {
    const videoParam = Number(searchParams.get('video'))
    if (videoParam) setViewingId(videoParam)
  }, [searchParams])

  const handleCloseView = () => {
    setViewingId(null)
    if (searchParams.has('video')) setSearchParams({}, { replace: true })
  }

  const handleDownload = (submission: ITestimonialVideoListItem) => {
    setDownloadingId(submission.id)
    download(() => service.download(submission.id), 'Erro ao baixar o vídeo').finally(() => setDownloadingId(null))
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await service.delete(deleting.id)
      toast.success('Vídeo de depoimento excluído')
      // Deleting the only row of a page would leave the table on a page that no longer exists.
      if (submissions.length === 1 && page > 1) setPage(page - 1)
      else fetchSubmissions()
      fetchCourses()
    } catch (e) {
      toast.error('Erro ao excluir o vídeo de depoimento', e)
    } finally {
      setDeleting(null)
    }
  }

  return (
    <>
      <PageTitle title="Vídeos de Depoimento" description="Vídeos de depoimento enviados pelos alunos" />

      <FilterBar onClear={clearFilters} isDirty={isDirty}>
        <FilterSearch
          value={filters.search}
          onChange={(search) => updateFilters({ search })}
          placeholder="Nome ou e-mail"
        />
        <FilterField label="Curso">
          <SearchableSelect
            options={courses.map((course) => ({ value: String(course.id), label: course.name }))}
            value={filters.courseId}
            onChange={(courseId) => updateFilters({ courseId })}
            placeholder="Todos os cursos"
            size="sm"
            isClearable
            entity="curso"
          />
        </FilterField>
        <FilterSelect
          label="Modalidade"
          emptyLabel="Todas"
          value={filters.modality}
          onChange={(modality) => updateFilters({ modality })}
          options={MODALITY_OPTIONS}
        />
        <FilterDateRange
          label="Data de envio"
          value={{ start: filters.dateStart, end: filters.dateEnd }}
          onChange={(date) => updateFilters({ dateStart: date.start, dateEnd: date.end })}
        />
      </FilterBar>

      <TableContainer>
        <Table mt="2">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>E-mail</Th>
              <Th>Curso</Th>
              <Th>Modalidade</Th>
              <Th>Cidade/UF</Th>
              <Th>Enviado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={COLUMNS} />
            ) : loadError ? (
              <TableErrorRow colSpan={COLUMNS} entity="vídeos de depoimento" onRetry={fetchSubmissions} />
            ) : submissions.length === 0 ? (
              <TableEmptyRow colSpan={COLUMNS} entity="vídeo de depoimento" />
            ) : (
              submissions.map((submission) => (
                <Tr key={submission.id}>
                  <Td>{submission.id}</Td>
                  <Td>{submission.name}</Td>
                  <Td>{submission.email}</Td>
                  <Td>
                    {submission.course_name ? (
                      <ShortText text={submission.course_name} beforeChars={30} afterChars={0} />
                    ) : (
                      <EmptyCell value={null} />
                    )}
                  </Td>
                  <Td>{submission.modality_label}</Td>
                  <Td>
                    {submission.city}/{submission.state}
                  </Td>
                  <Td>{formatDateTime(submission.created_at)}</Td>
                  <Td>
                    <ActionButton
                      action="view"
                      label="Assistir e ver detalhes"
                      onClick={() => setViewingId(submission.id)}
                    />
                    <ActionButton
                      action="download"
                      label="Baixar vídeo"
                      isLoading={downloadingId === submission.id}
                      onClick={() => handleDownload(submission)}
                    />
                    {permissions.edit && <ActionButton action="edit" onClick={() => setEditingId(submission.id)} />}
                    {permissions.delete && <ActionButton action="delete" onClick={() => setDeleting(submission)} />}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(p: number) => setPage(p)}
        pageSize={filters.length}
        onPageSizeChange={(pageSize) => {
          setFilters({ ...filters, length: pageSize })
          setPage(1)
        }}
      />

      <TestimonialVideoViewModal id={viewingId} onClose={handleCloseView} />
      <TestimonialVideoEditModal
        id={editingId}
        onClose={(updated) => {
          setEditingId(null)
          if (updated) {
            fetchSubmissions()
            fetchCourses()
          }
        }}
      />
      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        action="delete"
        entity="vídeo de depoimento"
        name={deleting?.name}
        warning="O arquivo do vídeo também será apagado do armazenamento."
      />

      <ActionFab label="Abrir o formulário de envio" to="/videos-de-depoimento/enviar" openInNewTab />
    </>
  )
}

export default TestimonialVideosPage
